import { useEffect, useState } from "react";
import { RunRate } from "./RunRate/RunRate";
import BusinessLeaderDashboardFilter from "./BusinessLeaderDashboardFilter/BusinessLeaderDashboardFilter";
import { AdditionalQueries } from "./AdditionalQueries/AdditionalQueries";
import { HighLevelKPI } from "./HighLevelKPI/HighLevelKPI";
import { Alert, Paper } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FloatingActionItems from "../../../../pages/FloatingItems/FloatingActionItems";
import { useSearchParams } from "react-router-dom";
import { HighLevelKpiSkeleton } from "./helpers/BLDSkeletons";

interface BuiLeaderDashBoardProps {
  siteOptions: any;
  getAllDimesnionsForViewOrganizationalSummary: any;
  getAllDashboardSectionsData: any;
  highLevelTargetActualData: any;
  sethighLevelTargetActualData: any;
  lastMonthTargetVsActualData: any;
  last7DaysSince2DaysAgoData: any;
  mtdQftdBudgetControlIdData: any;
  conversionLoadingPercLast14DaysData: any;
  isHighLevelLoading: boolean;
  isLastMonthLoading: boolean;
  isLast7DaysLoading: boolean;
  isMtdQftdLoading: boolean;
  isConversionLoading: boolean;
}

function BusinessLeaderDashboard({
  siteOptions,
  getAllDimesnionsForViewOrganizationalSummary,
  getAllDashboardSectionsData,
  highLevelTargetActualData,
  sethighLevelTargetActualData,
  lastMonthTargetVsActualData,
  last7DaysSince2DaysAgoData,
  mtdQftdBudgetControlIdData,
  conversionLoadingPercLast14DaysData,
  isHighLevelLoading,
  isLastMonthLoading,
  isLast7DaysLoading,
  isMtdQftdLoading,
  isConversionLoading,
}: BuiLeaderDashBoardProps) {
  // states for passing to the childrens components
  const [dataShow, setDataShow] = useState(false);
  const [highLevelData, setHighLevelData] = useState() as any;
  const [lastMonthData, setLastMonthData] = useState();
  const [last7DaysData, setLast7DaysData] = useState();
  //const [topKeyWordData, setTopKeyWordData] = useState();
  const [mtdQftdData, setMtdQftdData] = useState();
  const [conversionData, setConversionData] = useState();

  const [collapseAllSections, setCollapseAllSections] = useState(false);
  // Bumped on every "Get Data" so the self-fetching sections remount and refetch
  const [fetchToken, setFetchToken] = useState(0);

  const [searchParams] = useSearchParams();

  // Every section is requested at once, so the shells go up immediately and
  // each one swaps its own skeleton for data as its own call comes back.
  const onGetDataTriggered = () => {
    setFetchToken((token) => token + 1);
    setDataShow(true);
  };

  useEffect(() => {
    try {
      setHighLevelData(highLevelTargetActualData?.query_output);
    } catch (error: any) {
      console.error("Error getting BUI dashboard data", error);
    }
    // eslint-disable-next-line
  }, [highLevelTargetActualData]);

  useEffect(() => {
    if (lastMonthTargetVsActualData?.query_output?.length > 0) {
      setLastMonthData(lastMonthTargetVsActualData?.query_output);
    }
    // eslint-disable-next-line
  }, [lastMonthTargetVsActualData]);

  useEffect(() => {
    if (last7DaysSince2DaysAgoData?.query_output?.length > 0) {
      setLast7DaysData(last7DaysSince2DaysAgoData?.query_output);
    }
    // eslint-disable-next-line
  }, [last7DaysSince2DaysAgoData]);

  useEffect(() => {
    if (mtdQftdBudgetControlIdData?.query_output?.length > 0) {
      setMtdQftdData(mtdQftdBudgetControlIdData?.query_output);
    } else {
      if (mtdQftdBudgetControlIdData?.query_output?.length === 0) {
        setMtdQftdData(mtdQftdBudgetControlIdData?.query_output);
      }
    }
    // eslint-disable-next-line
  }, [mtdQftdBudgetControlIdData]);

  useEffect(() => {
    if (conversionLoadingPercLast14DaysData?.query_output?.length > 0) {
      setConversionData(conversionLoadingPercLast14DaysData?.query_output);
    }
    // eslint-disable-next-line
  }, [conversionLoadingPercLast14DaysData]);

  return (
    <>
      <>
        <BusinessLeaderDashboardFilter
          siteOptions={siteOptions}
          getAllDimesnionsForViewOrganizationalSummary={
            getAllDimesnionsForViewOrganizationalSummary
          }
          getAllDashboardSectionsData={getAllDashboardSectionsData}
          setCollapseAllSections={setCollapseAllSections}
          onGetDataTriggered={onGetDataTriggered}
          setHighLevelData={setHighLevelData}
          sethighLevelTargetActualData={sethighLevelTargetActualData}
          setDataShow={setDataShow}
        />
        <>
          {dataShow ? (
            <>
              <Paper>
                {isHighLevelLoading ? (
                  <HighLevelKpiSkeleton />
                ) : highLevelData?.length > 0 ? (
                  <>
                    <HighLevelKPI highLevelData={highLevelData} />
                    <RunRate highLevelData={highLevelData} />
                  </>
                ) : (
                  <Alert
                    sx={{ color: "#ED6C02", mb: 1 }}
                    severity="warning"
                    icon={
                      <InfoOutlinedIcon fontSize="inherit" sx={{ mr: 1 }} />
                    }
                  >
                    NO DATA FOR Run Rate AND High Level KPI
                  </Alert>
                )}
                <AdditionalQueries
                  setCollapseAllSections={setCollapseAllSections}
                  lastMonthData={lastMonthData}
                  last7DaysData={last7DaysData}
                  mtdQftdData={mtdQftdData}
                  conversionData={conversionData}
                  collapseAllSections={collapseAllSections}
                  fetchToken={fetchToken}
                  isLastMonthLoading={isLastMonthLoading}
                  isLast7DaysLoading={isLast7DaysLoading}
                  isMtdQftdLoading={isMtdQftdLoading}
                  isConversionLoading={isConversionLoading}
                />
              </Paper>
              <div data-tracker="396">
                {searchParams.get("selectedType") === "site" ? (
                  <FloatingActionItems
                    selectedSiteFloatingAction={
                      searchParams
                        .get("selectedSharedUids")
                        ?.split(",")[0] as string
                    }
                    selectedDimensionsFloatingAction={""}
                    trackIdFloatingAction={"435"}
                  />
                ) : (
                  <FloatingActionItems
                    selectedSiteFloatingAction={""}
                    selectedDimensionsFloatingAction={
                      searchParams
                        .get("selectedDimension")
                        ?.split(",")[0] as string
                    }
                    trackIdFloatingAction={"435"}
                  />
                )}
              </div>
            </>
          ) : (
            ""
          )}
        </>
      </>
    </>
  );
}

export default BusinessLeaderDashboard;
